/**
 * A aba "Histórico" do card da oportunidade (24/09/2026; tudo o que acontece
 * dentro da oportunidade desde 23/10/2026).
 *
 * Cada linha com a data, o que aconteceu e quem fez: a etapa de onde veio e
 * para onde foi, a nota (com o começo do texto), o documento (com o nome), a
 * consulta e a edição dos dados. Do mais recente ao mais antigo, para quem
 * abre a oportunidade do dia 2 ver tudo o que foi feito nela.
 *
 * São duas leituras: os passos do funil (`.../historico`) e as atividades
 * (`.../atividades`, rota própria). Na clínica, uma terceira, os passos lidos
 * do SISAC (`.../atendimentos`, #1151), que entram quando chegam. Se só as atividades falharem, os passos
 * aparecem, com o aviso. A tradução fica em `lib/linha-do-tempo.ts`.
 */

import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi, useAtendimentosDaOportunidade } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { Chip, Icone, type NomeDoIcone } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import { linhaDoTempo, type GrupoDoHistorico } from '../lib/linha-do-tempo';

const ICONE_DO_GRUPO: Record<GrupoDoHistorico, NomeDoIcone> = {
  etapa: 'seta',
  nota: 'nota',
  documento: 'documento',
  consulta: 'calendario',
  edicao: 'lapis',
};

function quandoPorExtenso(instante: string): string {
  return new Date(instante).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function HistoricoDoFunil({
  oportunidadeId,
  clinica = false,
}: {
  oportunidadeId: string;
  /** Na clínica, os passos que as rotinas trouxeram do SISAC entram junto (#1151). */
  clinica?: boolean;
}): ReactNode {
  const api = useApi();
  // A mesma consulta do "Resumo do atendimento": trocar de aba não lê o SISAC de novo.
  const atendimentos = useAtendimentosDaOportunidade(oportunidadeId, clinica);
  const historico = useQuery({
    queryKey: chaves.historicoDaOportunidade(oportunidadeId),
    enabled: api !== null,
    queryFn: ({ signal }) =>
      api!.historicoDaOportunidade({ caminho: { id: oportunidadeId }, sinal: signal }),
  });
  const atividades = useQuery({
    queryKey: chaves.atividadesDaOportunidade(oportunidadeId),
    enabled: api !== null,
    queryFn: ({ signal }) =>
      api!.atividadesDaOportunidade({ caminho: { id: oportunidadeId }, sinal: signal }),
  });

  if (historico.isPending || atividades.isPending) return <p>Buscando o histórico…</p>;
  if (historico.isError) {
    return (
      <Faixa tom="bloqueado">
        {recadoDeErro(historico.error, { generica: 'Não foi possível buscar o histórico agora.' })}
      </Faixa>
    );
  }
  const entradas = linhaDoTempo(
    historico.data.itens,
    atividades.isError ? [] : atividades.data.itens,
    // O SISAC fora não esconde o resto: os passos dele só ficam de fora.
    atendimentos.data?.itens ?? [],
  );

  return (
    <>
      {atividades.isError && (
        <Faixa tom="atencao">
          {recadoDeErro(atividades.error, {
            generica:
              'Não foi possível buscar as notas, os documentos e o resto do que aconteceu. As etapas aparecem abaixo.',
          })}
        </Faixa>
      )}
      {entradas.length === 0 ? (
        <p className="historico-op__vazio">Ainda não há nada registrado nesta oportunidade.</p>
      ) : (
        <ol className="historico-op" aria-label="Histórico da oportunidade">
          {entradas.map((entrada) => (
            <li key={entrada.chave} className="historico-op__passo">
              <time className="historico-op__quando" dateTime={entrada.quando}>
                {quandoPorExtenso(entrada.quando)}
              </time>
              <span className="historico-op__frase">
                <span className="historico-op__icone" aria-hidden="true">
                  <Icone nome={ICONE_DO_GRUPO[entrada.grupo]} />
                </span>
                {entrada.frase}
                {entrada.resultado === 'ganho' && <Chip>venda ganha</Chip>}
                {entrada.resultado === 'perdido' && <Chip tom="erro">venda perdida</Chip>}
              </span>
              {entrada.detalhes.map((detalhe) => (
                <span key={detalhe} className="historico-op__detalhe">
                  {detalhe}
                </span>
              ))}
              <span className="historico-op__por">{entrada.por}</span>
            </li>
          ))}
        </ol>
      )}
    </>
  );
}
